import introspectionQueryResultData from "@/../generated/fragmentTypes";
import {
  ApolloClient,
  DefaultContext,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { isSessionExpiredError } from "@/entities/keycloak";
import { UserFacingErrorCode } from "generated/apollo";
import * as pako from "pako";
import { toast } from "react-toastify";
import {
  getErrorLinkSkipCodes,
  GQLContextErrorSkipCodes,
} from "./errorLinkSkipCodes";
import { isAnalyticsOperation } from "./isAnalyticsOperation";
import { typeDefs } from "./localSchema";

export const cache = new InMemoryCache({
  possibleTypes: introspectionQueryResultData?.possibleTypes,
  typePolicies: {
    Client: {
      fields: {
        clientPermissions: {
          merge(_existing = [], incoming: unknown[]) {
            return incoming;
          },
        },
        clientFeatures: {
          merge(_existing = [], incoming: unknown[]) {
            return incoming;
          },
        },
      },
    },
  },
});

// await before instantiating ApolloClient, else queries might run before the cache is persisted

// if (0 && import.meta.env.DEV) {
//   await persistCache({
//     cache,
//     storage: new LocalStorageWrapper(window.localStorage),
//   });
// }

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const context: DefaultContext & Partial<GQLContextErrorSkipCodes> =
    operation.getContext();

  if (context.ignoreError) {
    return;
  }
  const errorLinkSkipCodes = getErrorLinkSkipCodes(context);

  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      if (
        errorLinkSkipCodes?.includes(
          error.extensions?.code as UserFacingErrorCode,
        )
      ) {
        return;
      }
      // Temporary, for the realm switch (EA-1775) — see PauseUserManagement.
      if (error.extensions?.code === "userManagementPaused") {
        toast.info(error.message);
        return;
      }

      const errorMessage = `${error.message}`;
      // hide deadlock errors for proposalLastEditor
      // because they are not relevant for the user
      if (
        errorMessage.includes("proposalLastEditor") &&
        errorMessage.includes("deadlock")
      ) {
        console.error(error);
        return;
      }
      if (!context.ignoreError) {
        toast.error(errorMessage);
      }
      console.error(error);
    });
  }
  if (networkError) {
    // The auth link stops requests once the session is gone and notifies the
    // user itself, so this is not a network fault worth reporting again.
    if (isSessionExpiredError(networkError)) {
      return;
    }
    if (!context.ignoreError) {
      toast.error(networkError.message);
    }
    const statusCode =
      "statusCode" in networkError ? networkError.statusCode : undefined;
    const result = "result" in networkError ? networkError.result : undefined;
    console.error(
      `[GraphQL network error] operation: ${operation.operationName}`,
      { statusCode, result },
      networkError,
    );
  }
});

// Custom fetch function that compresses the request
const isCompressionDisabled =
  sessionStorage.getItem("disableCompression") === "true";
const compressedFetch: typeof fetch = async function (uri, options) {
  // Set a threshold for compression (e.g., 10 KB)
  const COMPRESSION_THRESHOLD = 100 * 1024; // 100 KB
  if (
    !isCompressionDisabled &&
    options?.method === "POST" &&
    typeof options.body === "string" &&
    options.body.length > COMPRESSION_THRESHOLD
  ) {
    // Compress the request body
    const compressedBody = pako.gzip(options.body);

    // Modify the headers to indicate compressed content
    const headers = new Headers(options.headers);
    headers.set("Content-Encoding", "gzip");
    headers.set("Content-Type", "application/json");

    return fetch(uri, {
      ...options,
      body: compressedBody as BodyInit,
      headers: headers,
    });
  } else {
    // For non-POST requests or small payloads, use the regular fetch
    return fetch(uri, options);
  }
};

/**
 * Main backend HTTP link for regular queries and all mutations.
 */
const mainHttpLink = new HttpLink({
  uri: "/backend/graphql",
  fetch: compressedFetch,
});

/**
 * Analytics backend HTTP link for heavy analytics queries.
 * Routes analytics workloads to a separate backend to improve system resilience.
 */
const analyticsHttpLink = new HttpLink({
  uri: "/backend-analytics/graphql",
  fetch: compressedFetch,
});

/**
 * Split link that routes analytics queries to the analytics backend
 * and all other operations to the main backend.
 */
const httpLink = split(isAnalyticsOperation, analyticsHttpLink, mainHttpLink);

export const apolloClient = new ApolloClient({
  // https://www.apollographql.com/docs/react/api/core/ApolloClient#the-apolloclient-constructor
  link: from([errorLink, httpLink]),
  cache,
  typeDefs: [typeDefs],
  defaultOptions: {
    query: {
      // used for direct client.query() calls
      fetchPolicy: "network-only",
    },

    watchQuery: {
      /**
       * Note: The useQuery hook uses Apollo Client's watchQuery function.
       * To set defaultOptions when using the useQuery hook, make sure to set them under the defaultOptions.watchQuery property.
       * *https://www.apollographql.com/docs/react/api/core/ApolloClient#example-defaultoptions-object
       */
      fetchPolicy: "cache-and-network",
    },
    mutate: {
      onQueryUpdated: async (observableQuery) => {
        await observableQuery.refetch();
      },
    },
  },
});
